// Panel Şifreleri → "Yeni temsilci" ve satır başına "DARK şifresi" (2026-10-14, yalnız yönetici).
// Yeni temsilci tek formda: markanın telefon listesi (proxy — panel girişi + Ara), temsilci kaydı
// (DARK eşleşmesi + Canlı Durum) ve isteğe bağlı panel şifresi. DARK şifresi = getircall'daki
// dahilinin SIP şifresi (Santral → Dahili → Şifre); proxy kaydetmeden önce santrale sorar.
// Sunucu: src/api/new-hire.js. Şifreler burada saklanmaz; yanıtta da geri gelmez.

import { useState } from 'react';
import { createNewHire, updateDarkSecret } from '../api';
import { brandLabel } from '../constants';
import { useEscape } from '../hooks';
import type { BrandId, DarkSecretResult, NewHireResult, NewHireStep, NewHireUnit, PanelSyncStep } from '../types';
import { Icon, Modal, checkboxStyle, ghostBtnStyle, inputStyle, primaryBtnStyle, segStyle } from '../ui';

const FALLBACK_ERROR = 'İşlem başarısız';
const UNITS: NewHireUnit[] = ['CALL', 'VIP', 'PREVIP'];

/** Hat markaları (Prime VIP hattı, 2026-10-14): tek birim VIP, ve o hatta işe alınan HER temsilci panel
 *  kullanır (aktif + DARK şifresi zorunlu). Sunucu eşi src/api/new-hire.js HIRE_LINE_UNITS / isPanelHire. */
const LINE_UNITS: Partial<Record<BrandId, NewHireUnit[]>> = { primevip: ['VIP'] };
const unitsFor = (b: BrandId): NewHireUnit[] => LINE_UNITS[b] || UNITS;
const isPanelHire = (b: BrandId, u: NewHireUnit) => u === 'CALL' || Boolean(LINE_UNITS[b]);

const labelStyle = {
  display: 'block',
  fontSize: '11px',
  fontWeight: 600,
  color: 'var(--dim)',
  marginBottom: '7px',
  letterSpacing: '.04em',
} as const;

const hintStyle = { fontSize: '11.5px', color: 'var(--faint)', marginTop: '6px', lineHeight: 1.45 } as const;

const STEP_LABEL: Record<NewHireStep['key'], string> = {
  phone: 'Telefon / panel girişi',
  db: 'Temsilci kaydı',
  password: 'Panel şifresi',
  registry: "Kayıt sheet'i",
  binding: 'Kimlik bağlama',
};

const STEP_ICON: Record<NewHireStep['status'], { name: string; color: string }> = {
  done: { name: 'check_circle', color: '#3ec27a' },
  exists: { name: 'check_circle', color: 'var(--faint)' },
  skipped: { name: 'remove_circle_outline', color: 'var(--faint)' },
  warn: { name: 'warning', color: '#e0a23c' },
  error: { name: 'error', color: '#ef5d5d' },
};

function ErrorLine({ text }: { text: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '7px', fontSize: '12px', color: '#ef5d5d', lineHeight: 1.45 }}>
      <Icon name="error" size={15} style={{ marginTop: '1px' }} />
      <span>{text}</span>
    </div>
  );
}

function Steps({ steps }: { steps: NewHireStep[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
      {steps.map((s) => (
        <div key={s.key} style={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
          <Icon name={STEP_ICON[s.status].name} size={17} color={STEP_ICON[s.status].color} style={{ marginTop: '1px' }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text)' }}>{STEP_LABEL[s.key]}</div>
            <div style={{ fontSize: '11.5px', color: 'var(--dim)', lineHeight: 1.45, wordBreak: 'break-word' }}>{s.text}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Marka başına adım listesi — panel hesabı taşıma / şifre eşitleme (Ayarlar → Temsilciler, Panel Şifreleri).
 *  Metinler sunucudan TR gelir; EN'leri i18n.ts translatePhrase'deki "<Marka>: …" kalıbı çevirir. */
export function BrandSteps({ steps }: { steps: PanelSyncStep[] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
      {steps.map((s, i) => (
        <div key={s.brand + '|' + i} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
          <Icon name={STEP_ICON[s.status].name} size={16} color={STEP_ICON[s.status].color} style={{ marginTop: '1px' }} />
          <span style={{ fontSize: '11.5px', color: 'var(--dim)', lineHeight: 1.45, wordBreak: 'break-word' }}>{s.text}</span>
        </div>
      ))}
    </div>
  );
}

/** Şifre alanı + göster/gizle. Tarayıcı bunu sitenin şifresi sanıp kaydetmesin diye new-password. */
function SecretInput({
  value,
  onChange,
  placeholder,
  onEnter,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  onEnter?: () => void;
  autoFocus?: boolean;
}) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative' }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && onEnter) onEnter();
        }}
        type={show ? 'text' : 'password'}
        autoComplete="new-password"
        spellCheck={false}
        autoFocus={autoFocus}
        placeholder={placeholder}
        maxLength={128}
        style={{ ...inputStyle, paddingRight: '42px', fontFamily: show ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined }}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        title={show ? 'Gizle' : 'Göster'}
        style={{
          position: 'absolute',
          right: '6px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'transparent',
          border: 'none',
          color: 'var(--faint)',
          cursor: 'pointer',
          display: 'flex',
          padding: '4px',
        }}
      >
        <Icon name={show ? 'visibility_off' : 'visibility'} size={18} />
      </button>
    </div>
  );
}

function VerifyBox({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <div onClick={onToggle} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none' }}>
      <div style={checkboxStyle(on)}>{on && <Icon name="check" size={15} color="#fff" />}</div>
      <span style={{ fontSize: '12.5px', color: 'var(--text)' }}>Kaydetmeden önce santralde doğrula</span>
    </div>
  );
}

// ── Yeni temsilci ───────────────────────────────────────────────────────────

export function NewHireDialog({
  brands,
  initialBrand,
  onClose,
  onSaved,
}: {
  brands: BrandId[];
  initialBrand: BrandId;
  onClose: () => void;
  /** Liste yenilensin diye — kayıt başarılı (ya da kısmen) yazıldığında. */
  onSaved: (brand: BrandId) => void;
}): JSX.Element {
  const [brand, setBrand] = useState<BrandId>(initialBrand);
  const [name, setName] = useState('');
  const [unit, setUnit] = useState<NewHireUnit>(() => unitsFor(initialBrand)[0]);
  const [kod, setKod] = useState('');
  const [secret, setSecret] = useState('');
  const [verify, setVerify] = useState(true);
  const [panelPassword, setPanelPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [canSkip, setCanSkip] = useState(false);
  const [partial, setPartial] = useState<NewHireStep[]>([]);
  const [done, setDone] = useState<NewHireResult | null>(null);
  const [copied, setCopied] = useState(false);

  useEscape(true, () => {
    if (!busy) onClose();
  });

  const loginName = name.replace(/\s+/g, ' ').trim().toLocaleUpperCase('tr');
  const units = unitsFor(brand);
  const callUnit = isPanelHire(brand, unit);
  const lineBrand = Boolean(LINE_UNITS[brand]);
  const pickBrand = (b: BrandId) => {
    setBrand(b);
    // Bir hat markasına geçince birim onun tek birimi olur; çıkınca izinli değilse CALL'a döner.
    const allowed = unitsFor(b);
    if (!allowed.includes(unit)) setUnit(allowed[0]);
  };

  const submit = async (verifyOverride?: boolean) => {
    if (busy) return;
    if (!loginName) return setError('Ad gerekli');
    if (!/^[0-9]{2,6}$/.test(kod.trim())) return setError('Dahili numarası 2–6 rakam olmalı (ör. 695)');
    if (callUnit && !secret.trim()) return setError('DARK şifresi gerekli — getircall → Santral → Dahili → Şifre');
    if (panelPassword && panelPassword.length < 6) return setError('Panel şifresi en az 6 karakter olmalı');
    setBusy(true);
    setError('');
    setCanSkip(false);
    setPartial([]);
    try {
      const r = await createNewHire({
        brand,
        name: loginName,
        unit,
        kod: kod.trim(),
        secret: secret.trim(),
        verify: verifyOverride ?? verify,
        ...(panelPassword ? { panelPassword } : {}),
      });
      if (r.steps?.some((s) => s.status === 'done')) onSaved(brand);
      if (!r.ok) {
        setError(r.error || FALLBACK_ERROR);
        setCanSkip(r.canSkipVerify === true);
        // What already happened (a phone entry added before the DB step failed); the failure itself is the error line.
        setPartial((r.steps || []).filter((s) => s.status !== 'error'));
        return;
      }
      setSecret('');
      setPanelPassword('');
      setDone(r);
    } catch {
      setError(FALLBACK_ERROR);
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      /* izin yok — metin zaten ekranda */
    }
  };

  if (done) {
    const login = done.login;
    return (
      <Modal onClose={onClose} maxWidth={480}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '16px' }}>
          <Icon name="check_circle" size={22} color="#3ec27a" />
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>
            {done.name + ' kaydedildi · ' + brandLabel(done.brand || brand) + ' · dahili ' + (done.kod || kod)}
          </div>
        </div>
        <div style={{ marginBottom: '18px' }}>
          <Steps steps={done.steps || []} />
        </div>
        {login && (
          <>
            <label style={labelStyle}>PANEL GİRİŞİ</label>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <div style={{ ...inputStyle, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', flex: 1, userSelect: 'all' }}>
                {login.password ? login.name + ' · ' + login.password : login.name}
              </div>
              {login.password && (
                <button
                  onClick={() => void copy(login.password || '')}
                  title="Kopyala"
                  style={{ ...ghostBtnStyle, padding: '0 12px', display: 'flex', alignItems: 'center' }}
                >
                  <Icon name={copied ? 'check' : 'content_copy'} size={18} />
                </button>
              )}
            </div>
            <div style={{ ...hintStyle, marginTop: 0, marginBottom: '16px' }}>
              {login.custom
                ? 'Temsilci belirlediğiniz şifreyle girer.'
                : login.password
                  ? 'Temsilci Call Agent panelinde bu ad ve şifreyle girer; ilk girişte kendi şifresini belirler.'
                  : 'Temsilci telefon listesinde zaten vardı — mevcut şifresiyle girer.'}
            </div>
          </>
        )}
        <button onClick={onClose} style={{ ...primaryBtnStyle, width: '100%' }}>
          Tamam
        </button>
      </Modal>
    );
  }

  return (
    <Modal onClose={() => !busy && onClose()} maxWidth={480}>
      <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', marginBottom: '4px' }}>Yeni temsilci</div>
      <div style={{ fontSize: '12px', color: 'var(--dim)', marginBottom: '16px', lineHeight: 1.5 }}>
        Panel girişi, telefon (Ara) ve DARK eşleşmesi tek adımda oluşturulur.
      </div>

      {brands.length > 1 && (
        <div style={{ marginBottom: '14px' }}>
          <label style={labelStyle}>MARKA</label>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {brands.map((b) => (
              <button key={b} type="button" onClick={() => pickBrand(b)} disabled={busy} style={segStyle(brand === b)}>
                {brandLabel(b)}
              </button>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 130px', gap: '12px', marginBottom: '14px' }}>
        <div>
          <label style={labelStyle}>AD</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            autoComplete="off"
            spellCheck={false}
            maxLength={40}
            placeholder="ör. HAZAL"
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>DAHİLİ (KOD)</label>
          <input
            value={kod}
            onChange={(e) => setKod(e.target.value.replace(/\D+/g, ''))}
            inputMode="numeric"
            autoComplete="off"
            maxLength={6}
            placeholder="ör. 695"
            style={inputStyle}
          />
        </div>
      </div>

      <div style={{ marginBottom: '14px' }}>
        <label style={labelStyle}>BİRİM</label>
        <div style={{ display: 'flex', gap: '6px' }}>
          {units.map((u) => (
            <button key={u} type="button" onClick={() => setUnit(u)} disabled={busy} style={segStyle(unit === u)}>
              {u}
            </button>
          ))}
        </div>
        {lineBrand ? (
          <div style={hintStyle}>Prime VIP hattı: kayıt sheet'inde PRIME | VIP satırı; temsilci VIP panelinde giriş yapar — DARK şifresi gerekli.</div>
        ) : !callUnit ? (
          <div style={hintStyle}>VIP / PREVIP paneli kullanmaz — yalnız DARK eşleşmesi; DARK şifresi isteğe bağlı.</div>
        ) : null}
      </div>

      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>{callUnit ? 'DARK ŞİFRESİ' : 'DARK ŞİFRESİ (İSTEĞE BAĞLI)'}</label>
        <SecretInput value={secret} onChange={setSecret} placeholder="getircall → Santral → Dahili → Şifre" />
      </div>

      <div style={{ marginBottom: '16px' }}>
        <VerifyBox on={verify} onToggle={() => !busy && setVerify((v) => !v)} />
      </div>

      {(callUnit || secret) && (
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>PANEL ŞİFRESİ (İSTEĞE BAĞLI)</label>
          <SecretInput value={panelPassword} onChange={setPanelPassword} placeholder="boş bırakın: AD.123 — ilk girişte değiştirir" />
          {loginName && <div style={hintStyle}>{'Panel giriş adı: ' + loginName}</div>}
        </div>
      )}

      {partial.length > 0 && (
        <div style={{ marginBottom: '14px' }}>
          <Steps steps={partial} />
        </div>
      )}
      {error && (
        <div style={{ marginBottom: '14px' }}>
          <ErrorLine text={error} />
        </div>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={onClose} disabled={busy} style={{ ...ghostBtnStyle, flex: 1 }}>
          İptal
        </button>
        {canSkip && (
          <button onClick={() => void submit(false)} disabled={busy} style={{ ...ghostBtnStyle, flex: 2, color: '#e0a23c' }}>
            Doğrulamadan kaydet
          </button>
        )}
        <button onClick={() => void submit()} disabled={busy} style={{ ...primaryBtnStyle, flex: 2 }}>
          {busy ? (verify ? 'Santral doğrulanıyor…' : 'Kaydediliyor…') : 'Kaydet'}
        </button>
      </div>
    </Modal>
  );
}

// ── DARK şifresi (mevcut temsilci) ─────────────────────────────────────────────

export function DarkSecretDialog({
  brand,
  agentName,
  onClose,
}: {
  brand: BrandId;
  agentName: string;
  onClose: () => void;
}): JSX.Element {
  const [secret, setSecret] = useState('');
  const [verify, setVerify] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [canSkip, setCanSkip] = useState(false);
  const [doneText, setDoneText] = useState('');
  const [siblings, setSiblings] = useState<NonNullable<DarkSecretResult['siblings']>>([]);

  useEscape(true, () => {
    if (!busy) onClose();
  });

  const submit = async (verifyOverride?: boolean) => {
    if (busy) return;
    if (!secret.trim()) return setError('DARK şifresi gerekli');
    setBusy(true);
    setError('');
    setCanSkip(false);
    try {
      const r = await updateDarkSecret(brand, agentName, secret.trim(), verifyOverride ?? verify);
      if (!r.ok) {
        setError(r.error || FALLBACK_ERROR);
        setCanSkip(r.canSkipVerify === true);
        return;
      }
      setSecret('');
      setSiblings(Array.isArray(r.siblings) ? r.siblings : []);
      setDoneText(r.text || 'DARK şifresi güncellendi');
    } catch {
      setError(FALLBACK_ERROR);
    } finally {
      setBusy(false);
    }
  };

  if (doneText) {
    return (
      <Modal onClose={onClose} maxWidth={420}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '14px' }}>
          <Icon name="check_circle" size={22} color="#3ec27a" />
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)' }}>{agentName}</div>
        </div>
        <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '14px', lineHeight: 1.5 }}>{doneText}</div>
        {siblings.length > 0 && (
          // Kardeş hat (Prime ↔ Prime VIP): aynı dahili iki telefon listesinde — her birinin sonucu ayrı satır.
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
            {siblings.map((s) => (
              <div key={s.brand} style={{ display: 'flex', alignItems: 'flex-start', gap: '9px' }}>
                <Icon name={STEP_ICON[s.status].name} size={17} color={STEP_ICON[s.status].color} style={{ marginTop: '1px' }} />
                <div style={{ fontSize: '11.5px', color: 'var(--dim)', lineHeight: 1.45, wordBreak: 'break-word' }}>{s.text}</div>
              </div>
            ))}
          </div>
        )}
        <div style={{ ...hintStyle, marginTop: 0, marginBottom: '14px' }}>
          Açık paneller yeni şifreyi en geç bir sonraki girişte alır.
        </div>
        <button onClick={onClose} style={{ ...primaryBtnStyle, width: '100%' }}>
          Tamam
        </button>
      </Modal>
    );
  }

  return (
    <Modal onClose={() => !busy && onClose()} maxWidth={420}>
      <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text)', marginBottom: '12px' }}>DARK şifresi</div>
      <div style={{ fontSize: '12.5px', color: 'var(--dim)', marginBottom: '14px', lineHeight: 1.5 }}>
        <span style={{ color: 'var(--text)', fontWeight: 600 }}>{agentName}</span> için dahilinin yeni SIP şifresi
        (getircall → Santral → Dahili → Şifre). Dahili numarası değişmez.
      </div>
      <label style={labelStyle}>DARK ŞİFRESİ</label>
      <div style={{ marginBottom: '12px' }}>
        <SecretInput value={secret} onChange={setSecret} placeholder="yeni şifre" onEnter={() => void submit()} autoFocus />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <VerifyBox on={verify} onToggle={() => !busy && setVerify((v) => !v)} />
      </div>
      {error && (
        <div style={{ marginBottom: '14px' }}>
          <ErrorLine text={error} />
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px' }}>
        <button onClick={onClose} disabled={busy} style={{ ...ghostBtnStyle, flex: 1 }}>
          İptal
        </button>
        {canSkip && (
          <button onClick={() => void submit(false)} disabled={busy} style={{ ...ghostBtnStyle, flex: 2, color: '#e0a23c' }}>
            Doğrulamadan kaydet
          </button>
        )}
        <button onClick={() => void submit()} disabled={busy} style={{ ...primaryBtnStyle, flex: 2 }}>
          {busy ? (verify ? 'Santral doğrulanıyor…' : 'Kaydediliyor…') : 'Kaydet'}
        </button>
      </div>
    </Modal>
  );
}
